
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth';
import { 
  runCompleteAiPrayerMigration, 
  checkMigrationNeeded,
  getFallbackAiPrayerSettings,
  getFallbackFormSettings
} from '@/utils/aiPrayerMigration';

export function useAiPrayerMigration() {
  const { user } = useAuth();
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationCompleted, setMigrationCompleted] = useState(false);
  const [migrationError, setMigrationError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id && !migrationCompleted) {
      checkAndRunMigration(user.id);
    }
  }, [user?.id, migrationCompleted]);

  const checkAndRunMigration = async (userId: string) => {
    try {
      setIsMigrating(true);
      setMigrationError(null);

      // Check if migration is needed
      const needsMigration = await checkMigrationNeeded(userId);
      
      if (needsMigration) {
        console.log('Migration needed, starting migration process...');
        const success = await runCompleteAiPrayerMigration(userId);
        
        if (!success) {
          setMigrationError('Migration completed with some issues. Fallback mechanisms are active.');
        }
      }
      
      setMigrationCompleted(true);
    } catch (error) {
      console.error('Migration check/run failed:', error);
      setMigrationError('Migration failed. Using fallback settings.');
      setMigrationCompleted(true); // Still mark as completed to prevent infinite retries
    } finally {
      setIsMigrating(false);
    }
  };

  const getFallbackSettings = () => ({
    aiPrayerSettings: getFallbackAiPrayerSettings(),
    formSettings: getFallbackFormSettings()
  });

  return {
    isMigrating,
    migrationCompleted,
    migrationError,
    getFallbackSettings,
    retryMigration: user?.id ? () => checkAndRunMigration(user.id) : null
  };
}
